import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { CircularFramework, LinearFramework } from "@/components/FrameworkDiagram";
import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";

const STAGES = [
  { tag: "01", title: "Clarity", q: "Do we understand where we're going?",
    body: "Great organisations begin with clarity. Leaders need an honest understanding of where the organisation stands today, a shared view of where it is heading; your North Star, and a deep understanding of what truly creates value. Only then can assumptions be challenged, priorities agreed and confident decisions made." },
  { tag: "02", title: "Alignment", q: "Are we united behind the same future?",
    body: "Alignment is the discipline of shared understanding. It is not consensus; it is coherence — a leadership team that has surfaced its conflicts and chosen a direction it will defend together." },
  { tag: "03", title: "Execution", q: "Are we delivering what we agreed?",
    body: "Execution is where strategy meets reality. It is the discipline of turning intent into results. It is measured not by how busy an organisation is, but by whether it consistently delivers what it said it would, when it promised it by." },
  { tag: "04", title: "Value Realisation", q: "Are we becoming more valuable?",
    body: "Value Realisation is the discipline of converting leadership, investment and execution into measurable value. It asks whether decisions are strengthening the organisation, whether investments are generating meaningful returns, and whether the organisation is becoming more valuable year after year." },
  { tag: "05", title: "Choice", q: "Are we creating more strategic options?",
    body: "Choice is the reward of disciplined leadership. It is the least measured and, arguably, the most important dimension of organisational value. Organisations with more strategic options—whether to grow, invest, acquire, remain independent, transition ownership or exit—are inherently stronger because they control their future rather than react to it.\n\nLeadership is measured not by the decisions it makes today, but by the choices it creates for tomorrow." },
];

export default function Framework() {
  useSeo({
    title: "The Framework — Five Disciplines, One Cycle | Langwood360",
    description: "Clarity, Alignment, Execution, Value Realisation, Choice. The five disciplines of the Value Realisation Framework — read linearly, lived circularly.",
    path: "/framework",
  });
  return (
    <div className="min-h-screen bg-linen text-oxford">
      <Navbar />
      <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 pb-24">
        <div className="tick-label-accent mb-8">The Framework</div>
        <h1 className="display-serif text-5xl md:text-7xl max-w-4xl">
          A theory of value. Five disciplines, one cycle.
        </h1>
        <div className="rule-orange my-10" />
        <p className="max-w-2xl text-lg text-charcoal leading-relaxed">
          The Value Realisation Framework is the
          organising principle behind every asset. It is linear when explained,
          circular in practice.
        </p>
      </section>

      <section className="border-t border-oxford/10 bg-linen">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <div className="tick-label-accent mb-6">Read linearly</div>
            <LinearFramework />
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <div className="tick-label-accent mb-6">Live circularly</div>
            <CircularFramework />
            <p className="mt-6 text-charcoal max-w-lg italic">
              Once an organisation has more choices, it gains new clarity —
              which begins a new cycle. Great organisations do not complete
              this process. They live inside it.
            </p>
          </div>
        </div>
      </section>

      <section className="border-t border-oxford/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 space-y-24">
          {STAGES.map((s) => (
            <div key={s.tag} className="grid grid-cols-1 lg:grid-cols-12 gap-10">
              <div className="lg:col-span-3">
                <div className="text-orange font-mono text-6xl">{s.tag}</div>
                <div className="tick-label mt-3">Discipline</div>
              </div>
              <div className="lg:col-span-8 lg:col-start-5">
                <h3 className="display-serif text-4xl md:text-5xl">{s.title}</h3>
                <div className="tick-label mt-3 italic normal-case tracking-normal text-base text-charcoal font-serif">{s.q}</div>
                {s.body.split("\n\n").map((para, i) => (
                  <p key={i} className="mt-6 text-lg text-charcoal leading-relaxed max-w-2xl">{para}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-t border-oxford/10 bg-oxford text-linen">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="tick-label-accent mb-6">Take the diagnostic</div>
            <h2 className="display-serif text-4xl md:text-6xl">
              Twenty-five questions. Five dimensions. One number.
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <Link to="/diagnostic" data-testid="framework-cta" className="btn-orange">
              Take the Index
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
